
import React, { useEffect, useState } from "react";
import GenericList from "../shared/components/GenericList";
import { GridColDef } from "@mui/x-data-grid";
import { Grid2 } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import { fisyFanoratanaUrl } from "../../configs/URL";

export interface FicheFanoratana {
  id: number;
  Anarana: string;
  Zone: string;
  Adiresy: string;
  Fanondroana_rep_re: string;
  Telefaonina: string;
  Daty: string;
  Tohiny_Daty_fitsid_fanarahana_Eny_Tsia: string; // Eny ou Tsia
}

export const FicheFanoratanaTable: React.FC = () => {
  const [data, setData] = useState<FicheFanoratana[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Remplace ici par ton vrai endpoint API
        const res = await fetch(fisyFanoratanaUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        const mapped: FicheFanoratana[] = json.results.map((r: any, index: number) => ({
          id: r._id || index,
          Anarana: r.Anarana,
          Zone: r.Zone,
          Adiresy: r.Adiresy,
          Fanondroana_rep_re: r.Fanondroana_rep_re,
          Telefaonina: r.Telefaonina,
          Daty: r.Daty,
          Tohiny_Daty_fitsid_fanarahana_Eny_Tsia:
            r.Tohiny_Daty_fitsid_fanarahana_Eny_Tsia,
        }));

        setData(mapped);
      } catch (err: any) {
        setError(err.message || "Erreur de chargement");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <div>Chargement…</div>;
  if (error) return <div style={{ color: "red" }}>Erreur: {error}</div>;

  const columns: GridColDef[] = [
    { field: "Anarana", headerName: "Anarana (Nom)", width: 180 },
    { field: "Zone", headerName: "Zone", width: 140 },
    { field: "Adiresy", headerName: "Adiresy", width: 200 },
    { field: "Fanondroana_rep_re", headerName: "Fanondroana (Repère)", width: 200 },
    { field: "Telefaonina", headerName: "Téléfaonina", width: 140 },
    { field: "Daty", headerName: "Daty (Date d’inscription)", width: 180 },
    {
      field: "Tohiny_Daty_fitsid_fanarahana_Eny_Tsia",
      headerName: "Fanarahana (Eny/Tsia)",
      width: 200,
    },
  ];

  // Graphique 1 : nombre d'inscriptions par zone
  const zoneStats = Object.values(
    data.reduce((acc: any, x) => {
      if (!acc[x.Zone]) acc[x.Zone] = { Zone: x.Zone, count: 0 };
      acc[x.Zone].count++;
      return acc;
    }, {})
  );

  // Graphique 2 : répartition Eny/Tsia
  const fanarahanaStats = [
    {
      name: "Eny (Oui)",
      value: data.filter(
        (x) => x.Tohiny_Daty_fitsid_fanarahana_Eny_Tsia?.toLowerCase() === "eny"
      ).length,
    },
    {
      name: "Tsia (Non)",
      value: data.filter(
        (x) => x.Tohiny_Daty_fitsid_fanarahana_Eny_Tsia?.toLowerCase() === "tsia"
      ).length,
    },
  ];

  const COLORS = ["#82ca9d", "#ff6666"];

  return (
    <div style={{ padding: "16px" }}>
      <Grid2 container spacing={2} style={{ marginBottom: "16px" }}>
        <Grid2 size={6}>
          <h3 style={{ textAlign: "center", marginBottom: 10 }}>
            Nombre d'inscriptions par zone
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={zoneStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Zone" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" name="Inscriptions" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </Grid2>

        <Grid2 size={6}>
          <h3 style={{ textAlign: "center", marginBottom: 10 }}>
            Suivi effectué (Eny / Tsia)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={fanarahanaStats}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {fanarahanaStats.map((entry, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Grid2>
      </Grid2>

      <GenericList columns={columns} rows={data} />
    </div>
  );
};

export default FicheFanoratanaTable;
